/**
 * A client's webhooks: the switch that gives them to it, its switch and limits
 * per family, its endpoints, and the last day's deliveries (docs/39 §3).
 *
 * Off by default, and off means the client has no trace of them: no page, no
 * permission, nothing recorded for its endpoints. A family switched off here is
 * absent from the client's catalog and its endpoints receive nothing. A limit
 * left blank follows the platform's (Insight → Webhooks), shown beside it.
 * Staff may switch one endpoint off; only staff switch it back.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Webhook } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Input,
  QueryPending,
  ReadOnly,
  Select,
  Switch,
  api,
  formatNumber,
  formatRelative,
  toast,
  useCan,
} from '@connect/web-shared';

import {
  endpointState,
  familiesPayload,
  familyForm,
  limitText,
  limitValue,
  webhookKeys,
  type ClientWebhooks,
  type FamilyForm,
} from './webhooks';

const SWITCH_OPTIONS = [
  { value: '', label: 'As the platform' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];

export function ClientWebhooksCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.webhook.edit');
  const url = `/messaging/clients/${clientPublicId}/webhooks`;
  const key = webhookKeys.client(clientPublicId);
  const settings = useQuery({ queryKey: key, queryFn: () => api.get<ClientWebhooks>(url) });
  const data = settings.data;

  const [enabled, setEnabled] = useState(false);
  const [total, setTotal] = useState('');
  const [families, setFamilies] = useState<Record<string, FamilyForm>>({});
  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setTotal(limitText(data.max_endpoints_override));
    setFamilies(Object.fromEntries(data.families.map((family) => [family.id, familyForm(family)])));
  }, [data]);

  const payload = familiesPayload(families);
  const totalValue = limitValue(total);
  const invalid = payload === null || Number.isNaN(totalValue);

  const save = useMutation({
    mutationFn: () =>
      api.put<ClientWebhooks>(url, { enabled, max_endpoints: totalValue, families: payload ?? {} }),
    onSuccess: (saved) => {
      queryClient.setQueryData(key, saved);
      toast.success('Saved', saved.enabled ? `Webhooks are on for ${clientName}.` : `Webhooks are off for ${clientName}.`);
    },
    onError: (caught) => toast.error('Could not save', caught instanceof ApiError ? caught.message : undefined),
  });
  const setEndpoint = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'enable' | 'disable' }) =>
      api.put(`${url}/${id}/status`, { action }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
    onError: (caught) => toast.error('Could not change it', caught instanceof ApiError ? caught.message : undefined),
  });

  const field = (family: string, name: keyof FamilyForm, value: string) =>
    setFamilies((current) => {
      const base = current[family] ?? { enabled: '', max_endpoints: '', max_events_per_endpoint: '', max_per_minute: '' };
      return { ...current, [family]: { ...base, [name]: value } as FamilyForm };
    });

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Webhook className="size-4" />
            Webhooks
          </span>
        }
        description={`Connect calls ${clientName}'s own systems when something happens on its account or its services.`}
      />
      <CardBody className="space-y-5">
        {data === undefined ? (
          <QueryPending query={settings} label="Loading webhooks" />
        ) : (
          <>
            {!data.platform_enabled ? (
              <Alert tone="warning" title="Webhooks are off for the whole installation">
                Nothing is recorded or sent for any client until they are switched back on under Insight → Webhooks.
              </Alert>
            ) : null}
            <ReadOnly when={!canEdit}>
              <div className="space-y-4">
                <Switch
                  label={`Webhooks for ${clientName}`}
                  checked={enabled}
                  hint={
                    enabled
                      ? `${clientName} sets up endpoints under Webhooks in its app, with or without API keys.`
                      : `Off: ${clientName} sees nothing of webhooks, and nothing is sent to endpoints it made.`
                  }
                  onChange={(event) => setEnabled(event.target.checked)}
                />
                <Input
                  label="Endpoints in all"
                  value={total}
                  inputMode="numeric"
                  placeholder={String(data.max_endpoints)}
                  hint={`Blank follows the platform: ${data.max_endpoints_override === null ? data.max_endpoints : 'its own setting'}.`}
                  onChange={(event) => setTotal(event.target.value)}
                  containerClassName="max-w-xs"
                />
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">By family</h3>
                  <ul className="divide-y divide-line rounded-control border border-line">
                    {data.families.map((family) => {
                      const form = families[family.id] ?? familyForm(family);
                      return (
                        <li key={family.id} className="grid gap-3 px-3 py-3 sm:grid-cols-[minmax(0,1fr)_repeat(4,minmax(0,8rem))]">
                          <div className="min-w-0 text-sm">
                            <p className="font-medium text-heading">{family.kind === 'account' ? 'Account' : family.label}</p>
                            <p className="text-xs text-muted">
                              {family.endpoints} endpoint{family.endpoints === 1 ? '' : 's'}
                              {family.offered ? '' : ' · the client has no such service now'}
                              {family.enabled ? '' : ' · off'}
                            </p>
                          </div>
                          <Select
                            label="Switch"
                            size="sm"
                            value={form.enabled}
                            options={SWITCH_OPTIONS}
                            onChange={(event) => field(family.id, 'enabled', event.target.value)}
                          />
                          <Input
                            label="Endpoints"
                            size="sm"
                            inputMode="numeric"
                            value={form.max_endpoints}
                            placeholder={String(family.limits.max_endpoints)}
                            onChange={(event) => field(family.id, 'max_endpoints', event.target.value)}
                          />
                          <Input
                            label="Events each"
                            size="sm"
                            inputMode="numeric"
                            value={form.max_events_per_endpoint}
                            placeholder={family.limits.max_events_per_endpoint === null ? 'All' : String(family.limits.max_events_per_endpoint)}
                            onChange={(event) => field(family.id, 'max_events_per_endpoint', event.target.value)}
                          />
                          <Input
                            label="Per minute"
                            size="sm"
                            inputMode="numeric"
                            value={form.max_per_minute}
                            placeholder={String(family.limits.max_per_minute)}
                            onChange={(event) => field(family.id, 'max_per_minute', event.target.value)}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
                {invalid ? <Alert tone="danger" title="Each limit is a whole number, or blank" /> : null}
                <Can permission="messaging.webhook.edit">
                  <Button loading={save.isPending} disabled={invalid} onClick={() => save.mutate()}>
                    Save
                  </Button>
                </Can>
              </div>
            </ReadOnly>

            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">
                Last 24 hours · {formatNumber(data.usage.delivered ?? 0)} delivered · {formatNumber(data.usage.failed ?? 0)} failed ·{' '}
                {formatNumber((data.usage.pending ?? 0) + (data.usage.sending ?? 0))} waiting
              </h3>
              {data.webhooks.length ? (
                <ul className="divide-y divide-line rounded-control border border-line">
                  {data.webhooks.map((hook) => {
                    const state = endpointState(hook);
                    const staffOff = hook.status === 'disabled' && hook.disabled_reason === 'staff';
                    return (
                      <li key={hook.public_id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2 text-sm">
                        <div className="min-w-0 space-y-1">
                          <p className="break-all font-mono text-xs text-heading">{hook.url}</p>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge tone="neutral">{hook.family}</Badge>
                            <Badge tone={state.tone}>{state.label}</Badge>
                            {hook.last_failed_at && (!hook.last_delivered_at || hook.last_failed_at > hook.last_delivered_at) ? (
                              <Badge tone="danger">
                                Failing {formatRelative(hook.last_failed_at)}: {hook.last_error}
                              </Badge>
                            ) : hook.last_delivered_at ? (
                              <Badge tone="success">Delivered {formatRelative(hook.last_delivered_at)}</Badge>
                            ) : null}
                          </div>
                        </div>
                        <Can permission="messaging.webhook.edit">
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={setEndpoint.isPending}
                            onClick={() => setEndpoint.mutate({ id: hook.public_id, action: staffOff ? 'enable' : 'disable' })}
                          >
                            {staffOff ? 'Switch back on' : 'Switch off'}
                          </Button>
                        </Can>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-muted">{clientName} has no endpoints.</p>
              )}
            </section>
          </>
        )}
      </CardBody>
    </Card>
  );
}
